import { FC, useMemo } from "react";
import { ComplianceConnectFilterBar } from "@app/compliance-connect/common/ComplianceConnectFilterBar";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ClientListItem } from "@app/client/client";
import { NetworkSubmissionFilterOption, NetworkSubmissionFilterOptions, NetworkSubmissionFilters } from "@app/compliance-connect/network-submission/interfaces";
import { SpecialtyGroupExceptionStatus, SpecialtyGroupExceptionStatusLabels } from "@app/exceptions/enums";

interface NetworkSubmissionFilterBarProps {
  pendingFilters: NetworkSubmissionFilters;
  filterOptions: NetworkSubmissionFilterOptions | null;
  regulatorOptions: ClientListItem[] | null;
  onPendingFiltersChange: (filters: NetworkSubmissionFilters) => void;
  onSearch: () => void;
  onClearAll: () => void;
}

const REVIEW_STATUS_ALL = "all";

function optionsToDropdownItems(options: NetworkSubmissionFilterOption[]): DropdownItem<string>[] {
  return options.map(o => ({ key: o.value, name: o.displayName }));
}

function selectionText(selectedCount: number, totalCount: number): string {
  return `${selectedCount} of ${totalCount} options selected`;
}

export const NetworkSubmissionFilterBar: FC<NetworkSubmissionFilterBarProps> = ({
  pendingFilters,
  filterOptions,
  regulatorOptions,
  onPendingFiltersChange,
  onSearch,
  onClearAll,
}) => {
  const specialtyItems = useMemo(
    () => optionsToDropdownItems(filterOptions?.specialties ?? []),
    [filterOptions],
  );

  const { regulatorItems, regulatorNameByKey } = useMemo(() => {
    const items = (regulatorOptions ?? []).map(r => ({ key: r.clientId, name: r.name } as DropdownItem<number>));
    return { regulatorItems: items, regulatorNameByKey: new Map(items.map(item => [item.key, item.name])) };
  }, [regulatorOptions]);

  const serviceAreaItems = useMemo(
    () => (filterOptions?.serviceAreas ?? []).map(state => ({
      checkboxStatus: null,
      children: (state.children ?? []).map(county => ({ key: county.value, name: county.displayName })),
      key: state.value,
      name: state.displayName,
    } as NestingParentDropdownItem)),
    [filterOptions],
  );

  const specialtyNameByKey = useMemo(
    () => new Map(specialtyItems.map(item => [item.key, item.name])),
    [specialtyItems],
  );

  return (
    <ComplianceConnectFilterBar
      clearAllTestId="ns-filter-clear-all"
      containerTestId="network-submission-filter-bar"
      disabled={!filterOptions || !regulatorOptions}
      extraFilters={
        <>
          <div className="compliance-connect-filter-bar__item">
            <MultiselectDropdown<number>
              containerClassName="compliance-connect-filter-bar__item"
              customSelectInputText={selectionText(pendingFilters.regulatorClientIds.length, regulatorItems.length)}
              disabled={!regulatorOptions}
              items={regulatorItems}
              label="Regulator Name"
              onSelectionChanged={selectedItems => onPendingFiltersChange({ ...pendingFilters, regulatorClientIds: selectedItems.map(x => x.key) })}
              selectedItems={pendingFilters.regulatorClientIds.map(key => ({ key, name: regulatorNameByKey.get(key) ?? key.toString() }))}
              showTooltipOnOverflow
              testId="ns-filter-regulator-name"
            />
          </div>
          <div className="compliance-connect-filter-bar__item">
            <QESSelect
              label="Regulatory Review Status"
              testId="ns-filter-review-status"
              value={pendingFilters.regulatoryReviewStatus ?? REVIEW_STATUS_ALL}
              onChange={e =>
                onPendingFiltersChange({
                  ...pendingFilters,
                  regulatoryReviewStatus:
                    e.target.value === REVIEW_STATUS_ALL
                      ? null
                      : (e.target.value as SpecialtyGroupExceptionStatus),
                })
              }
            >
              <option value={REVIEW_STATUS_ALL}>All</option>
              {Object.values(SpecialtyGroupExceptionStatus).map(s => (
                <option key={s} value={s}>
                  {SpecialtyGroupExceptionStatusLabels[s]}
                </option>
              ))}
            </QESSelect>
          </div>
        </>
      }
      onClearAll={onClearAll}
      onSearch={onSearch}
      onServiceAreasChange={keys => onPendingFiltersChange({ ...pendingFilters, serviceAreas: keys })}
      onSpecialtiesChange={selectedItems => onPendingFiltersChange({ ...pendingFilters, specialties: selectedItems.map(x => x.key) })}
      searchTestId="ns-filter-search"
      selectedServiceAreas={pendingFilters.serviceAreas}
      selectedSpecialties={pendingFilters.specialties.map(key => ({ key, name: specialtyNameByKey.get(key) ?? key }))}
      serviceAreaOptions={serviceAreaItems}
      serviceAreaTestId="ns-filter-service-area"
      serviceAreaVariant="nested"
      specialtyOptions={specialtyItems}
      specialtyTestId="ns-filter-specialty"
    />
  );
};
